import { useEffect, useState } from "react";
import { useNavigation } from "@/stores/useNavigation";
import { PlasmaOrb, XaesTitle } from "@/components/shell/XaesEntryShell";

/**
 * Full-screen entry sequence shown to authenticated users before they
 * select Mission Control. Ports the `#opening-screen` block from
 * `_legacy/mc.html` lines 82-112.
 *
 * Vertical placement is driven by `window.matchMedia` (not container
 * queries or Tailwind max-* alone): under Windows OS-scale the layout
 * container can measure wider than the emulated CSS viewport, so CSS
 * phone rules never fired and Click to begin looked stuck.
 */
export function OpeningScreen() {
  const setActivePanel = useNavigation((s) => s.setActivePanel);
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 700px)");
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  function handleEnter() {
    setActivePanel("MC_00");
  }

  return (
    <button
      type="button"
      onClick={handleEnter}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleEnter();
        }
      }}
      className="text-foreground absolute inset-0 z-10 cursor-pointer"
      aria-label="Begin XAES Phase Zero"
    >
      <div
        className="absolute left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
        style={{
          // 32% centers the stack in the dark TV band at both tiers —
          // was 42% on desktop, calibrated against the old
          // `bg-mission.jpg`. The background swapped to
          // `bg-mission-2.png` (MissionControl.tsx) without re-tuning
          // this, so desktop drifted down onto the lower half of the
          // screen; 32% matches the new photo's TV screen at both
          // tiers, same as narrow already did.
          top: "32%",
          gap: narrow ? 6 : undefined,
        }}
      >
        <XaesTitle />
        <PlasmaOrb />
        <div
          className="font-sans text-[clamp(10px,0.65vw,15px)] font-medium uppercase whitespace-nowrap"
          style={{
            letterSpacing: "clamp(3px, 0.4vw, 9px)",
            color: "rgba(var(--accent-sky-rgb), 0.9)",
            marginTop: "clamp(8px, 0.8vw, 18px)",
            opacity: 0,
            textShadow:
              "0 0 10px rgba(var(--accent-sky-rgb), 0.8), 0 0 24px rgba(var(--accent-true-blue-rgb), 0.5)",
            animation:
              "xaes-fade-in-pulse 1.8s ease 1.2s both, xaes-pulse-text 2.5s ease-in-out 3s infinite",
          }}
        >
          Click to begin
        </div>
      </div>
    </button>
  );
}
